import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { storeHandler } from "../../api/store/storeActions";
import { Checkbox } from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import "./mediaTypeFilter.css";

interface MediaTypeFilterProps {
  className?: string;
}

export function MediaTypeFilter({ className }: MediaTypeFilterProps) {
  const dispatch = useDispatch<any>();
  const selectedMediaTypes = useSelector((state: any) => state.data.mediaType) || [];
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const mediaTypes = [
    { value: "all", label: "All Items" },
    { value: "video", label: "Video" },
    { value: "ppt", label: "Slide" },
    { value: "image", label: "Static" },
  ];

  const [localSelection, setLocalSelection] = useState<string[]>(selectedMediaTypes);

  useEffect(() => {
    setLocalSelection(selectedMediaTypes);
  }, [selectedMediaTypes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideButton = buttonRef.current?.contains(target);
      const isClickInsideMenu = (event.target as Element)?.closest('.media-type-dropdown-menu');
      
      if (!isClickInsideButton && !isClickInsideMenu) {
        setIsOpen(false);
      }
    };

    // Add event listener with slight delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleMediaTypeToggle = (value: string) => {
    let newSelection: string[];

    if (value === "all") {
      // "All Items" means no filter - clear all selections
      newSelection = [];
    } else {
      // Toggle individual media type
      if (localSelection.includes(value)) {
        // Remove from selection
        newSelection = localSelection.filter((type) => type !== value);
      } else {
        // Add to selection
        newSelection = [...localSelection, value];
      }
      
      // Get all selectable media types (excluding "all")
      const selectableTypes = mediaTypes.filter((mt) => mt.value !== "all");
      
      // If all media types are now selected, convert to "All Items" (empty selection)
      const allSelected = selectableTypes.every((mt) => newSelection.includes(mt.value));
      if (allSelected) {
        newSelection = [];
      }
      
      // Close dropdown when clicking on a single media type
      setIsOpen(false);
    }

    setLocalSelection(newSelection);
    dispatch(storeHandler({ mediaType: newSelection }));
  };

  const isSelected = (value: string) => {
    if (value === "all") {
      // "All Items" is selected if no specific types are selected (no filter)
      // OR if all media types are selected
      if (localSelection.length === 0) {
        return true;
      }
      // Check if all selectable media types are selected
      const selectableTypes = mediaTypes.filter((mt) => mt.value !== "all");
      return selectableTypes.every((mt) => localSelection.includes(mt.value));
    }
    return localSelection.includes(value);
  };

  const getDisplayLabel = () => {
    if (localSelection.length === 0) {
      return "All Items";
    }

    // Get all selectable media types (excluding "all")
    const selectableTypes = mediaTypes.filter((mt) => mt.value !== "all");
    
    // Check if all media types are selected
    const allSelected = selectableTypes.every((mt) => localSelection.includes(mt.value));
    
    if (allSelected) {
      return "All Items";
    }

    const labelsInDisplayOrder = mediaTypes
      .filter((mt) => localSelection.includes(mt.value) && mt.value !== "all")
      .map((mt) => mt.label);

    if (labelsInDisplayOrder.length === 0) {
      return "All Items";
    }

    return labelsInDisplayOrder.join(", ");
  };

  // Calculate dropdown position for portal rendering
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const getResponsiveWidth = () => {
        const windowWidth = window.innerWidth;
        // Match CSS breakpoints: mobile (<640px), tablet (641-1024px), desktop (>1024px)
        if (windowWidth <= 640) {
          return 120;
        } else if (windowWidth <= 1024) {
          return 130;
        }
        return 140;
      };

      const updatePosition = () => {
        if (buttonRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          // Use getBoundingClientRect which already accounts for scroll position
          // Add small offset for spacing below button
          const dropdownWidth = getResponsiveWidth();
          const maxLeft = window.innerWidth - dropdownWidth - 8;
          const adjustedLeft = Math.min(buttonRect.left, Math.max(8, maxLeft));

          setDropdownPosition({
            top: buttonRect.bottom + 4,
            left: adjustedLeft,
            width: dropdownWidth
          });
        }
      };
      
      // Initial position calculation
      updatePosition();
      
      // Update on scroll and resize
      const handleScroll = () => {
        updatePosition();
      };
      
      const handleResize = () => {
        updatePosition();
      };
      
      // Listen to scroll on window and all scrollable parents
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      // Also listen to scroll on document
      document.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('scroll', handleScroll, true);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  const renderDropdownMenu = () => {
    const menuContent = (
      <div 
        className="media-type-dropdown-menu"
        style={dropdownPosition ? {
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 99999,
          transform: 'none'
        } : {}}
      >
        {mediaTypes.map((type) => {
          const selected = isSelected(type.value);
          return (
            <div
              key={type.value}
              className={`media-type-menu-item ${selected ? "media-type-menu-item-selected" : ""}`}
              onClick={() => handleMediaTypeToggle(type.value)}
            >
              <Checkbox
                checked={selected}
                onChange={() => handleMediaTypeToggle(type.value)}
                className="media-type-checkbox"
                sx={{
                  color: '#020202ffff',
                  '&.Mui-checked': {
                    color: '#d32f2f',
                  },
                  padding: '4px',
                }}
              />
              <span className={`media-type-menu-label ${selected ? "media-type-menu-label-selected" : ""}`}>
                {type.label}
              </span>
            </div>
          );
        })}
      </div>
    );

    // Use portal for integrated filter to escape container clipping
    if (className?.includes('integrated-filter') && isOpen && dropdownPosition) {
      return createPortal(menuContent, document.body);
    }

    // Regular rendering for non-integrated
    return isOpen ? menuContent : null;
  };

  return (
    <div className={`media-type-filter-dropdown ${className || ""}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        className="media-type-dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{getDisplayLabel()}</span>
        <KeyboardArrowDown 
          className={`dropdown-arrow ${isOpen ? "open" : ""}`}
          style={{ fontSize: '1rem', color: '#6b7280' }}
        />
      </button>
      
      {!className?.includes('integrated-filter') && isOpen && (
        <div className="media-type-dropdown-menu">
          {mediaTypes.map((type) => {
            const selected = isSelected(type.value);
            return (
              <div
                key={type.value}
                className={`media-type-menu-item ${selected ? "media-type-menu-item-selected" : ""}`}
                onClick={() => handleMediaTypeToggle(type.value)}
              >
                <Checkbox
                  checked={selected}
                  onChange={() => handleMediaTypeToggle(type.value)}
                  className="media-type-checkbox"
                  sx={{
                    color: '#d32f2f',
                    '&.Mui-checked': {
                      color: '#d32f2f',
                    },
                    padding: '4px',
                  }}
                />
                <span className={`media-type-menu-label ${selected ? "media-type-menu-label-selected" : ""}`}>
                  {type.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
      
      {className?.includes('integrated-filter') && renderDropdownMenu()}
    </div>
  );
}